import React, {CSSProperties, ReactNode} from 'react';

import {
    List,
    ListItemData,
    Loader,
    Popup,
    PopupPlacement,
    TextInput,
    TextInputPin,
    TextInputProps,
    TextInputSize,
} from '@gravity-ui/uikit';
import debounce from 'lodash/debounce';
import _sortBy from 'lodash/sortBy';
import throttle from 'lodash/throttle';
import uniqueId from 'lodash/uniqueId';

import {block} from '../cn';
import {OutsideFocusHandlerContainer} from '../useOnFocusOutside/OutsideFocusHandlerContainer';

import i18n from './i18n';

import './Suggest.scss';

const b = block('suggest');

export type RenderStyle = 'popup' | 'inline';

export interface SuggestProps<T = any> {
    /** Текст в поле ввода */
    text: string;
    /** Функция, реагирующая на изменения текста `text` в поле ввода */
    onUpdate: (item: string) => void;
    /** Функция, принимающая текст из поля ввода и возвращающая новый список элементов саджеста или `Promise` */
    getItems: (text: string) => ListItemData<T>[] | Promise<ListItemData<T>[]>;
    /** Обработчик клика по элементу саджеста */
    onItemClick?: (item: T, index?: number, fromKeyboard?: boolean) => boolean | void;
    /** Функция для кастомного лейаута саджеста. */
    children?: (input: ReactNode, items: ListItemData<T>[], loading: boolean) => ReactNode;
    /** Placeholder поля ввода */
    placeholder?: string;
    /** Сделать поле ввода неактивным */
    disabled?: boolean;
    /** Атрибут `autocomplete` поля ввода */
    autoComplete?: TextInputProps['autoComplete'];
    /** Включить автофокус поля ввода */
    autoFocus?: boolean;
    /** Микс для имени класса */
    className?: string;
    popupPlacement?: PopupPlacement;
    /** Микс для имени класса попапа */
    popupClassName?: string;
    /** Микс для имени класса импута */
    inputClassName?: string;
    /** Высота элемента саджеста в `px` или функция, возвращающая значение высоты для элемента саджеста: `(item: any) => number`. Используется только при `virtualized: true`. */
    itemHeight?: number | ((item: ListItemData<T>) => number);
    /** Флаг, указывающий, что значение из поля ввода должно быть обработано, и не обязательно должен быть выбран элемент из саджеста. Должен быть указан `onInputEnterKeyDown`. */
    applicableInputValue?: boolean;
    /** Запрашивать список элементов саджеста при начальной отрисовке компонента */
    getItemsOnMount?: boolean;
    /** Показывать элементы саджеста при пустом поле ввода */
    showItemsOnNoText?: boolean;
    /** Обработчик нажатия Enter в поле ввода. Если не указан, то по Enter будет срабатывать `onItemClick` на активном элементе саджеста. Используется только при `applicableInputValue: true`. */
    onInputEnterKeyDown?: (text: string, event: React.KeyboardEvent) => void;
    /** Обработчик нажатия любой клавиши в поле ввода */
    onInputKeyDown?: (text: string, event: React.KeyboardEvent) => void;
    /** Функция, которая будет вызвана на блюр текстового инпута */
    onBlur?: () => void;
    /** Включить синхронизацию ширины поля ввода и попапа саджеста при ресайзе окна */
    syncPopupOnResize?: boolean;
    /** Генератор кастомного сообщения о пустом списке элементов на основе текущего значения поля ввода */
    getNoItemsMessage?: (text: string) => ReactNode;
    /** Выключить показ сообщение об отсутствии совпадений */
    showNoItemsMessage?: boolean;
    /** Размер поля ввода */
    size?: TextInputSize;
    /** Включить виртуализацию. При выключенном флаге будут отрисованы все элементы саджеста разом. */
    virtualized?: boolean;
    /** Функция рендера, получающая на вход элемент саджеста и возвращающая React-ноду */
    renderItem?: (item: T) => ReactNode;
    /** Время в мс для отложенного вызова `getItems` после `onUpdate` */
    debounce?: number;
    /** Значение пробрасывается в TextInput.props.pin */
    pin?: TextInputPin;
    id?: string;
    renderStyle?: RenderStyle;
    /** Кнопка очистки инпута */
    hasClear?: boolean;
    /** Функция, высчитывающая какой Item сделать активным после резолва getItems */
    getInitialActiveItemIndex?: (items: ListItemData<T>[]) => number;
    /** Значение пробрасывается в TextInput.props.error */
    error?: TextInputProps['error'];
    /** Дополнительные пропсы для контрола */
    controlProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

interface SuggestState<T> {
    progress: boolean;
    focused: boolean;
    items: ListItemData<T>[];
    progressTimerId: ReturnType<typeof setTimeout> | null;
    activeIndex?: number;
}

export class Suggest<T = any> extends React.PureComponent<SuggestProps<T>, SuggestState<T>> {
    static defaultProps: Partial<SuggestProps> = {
        virtualized: false,
        renderItem: (item) => item,
        debounce: 0,
        renderStyle: 'popup',
        id: uniqueId('suggest-'),
    };

    static getDerivedStateFromProps<Props extends SuggestProps>(nextProps: Props) {
        if (!nextProps.showItemsOnNoText && !nextProps.text) {
            return {
                items: [],
            };
        }

        return null;
    }

    state: SuggestState<T> = {
        progress: false,
        focused: false,
        items: [],
        progressTimerId: null,
    };

    throttledForceUpdate = throttle(() => this.forceUpdate(), 300);

    textInputRef = React.createRef<HTMLInputElement>();
    inputRef = React.createRef<HTMLDivElement>();

    listRef = React.createRef<List<T>>();

    latestPromise?: Promise<T[]>;

    _getItems = debounce(async (text: string) => {
        let items: ListItemData<T>[] = [];

        const result = this.props.getItems(text);

        if (result instanceof Promise) {
            this.latestPromise = result;
        }

        try {
            items = await result;
        } catch (error) {}

        if (result instanceof Promise) {
            // Exit if this promise is different of the latest one
            if (result !== this.latestPromise) {
                return;
            }

            delete this.latestPromise;
        }

        this.setState({items});
        this.clearProgress();

        if (items.length && !this.props.applicableInputValue && this.listRef.current) {
            if (this.props.getInitialActiveItemIndex) {
                const index = this.props.getInitialActiveItemIndex(items) || 0;
                this.listRef.current.activateItem(index);
                this.setState({activeIndex: index});
            } else {
                const keyDownEvent = new KeyboardEvent('keydown', {key: 'ArrowDown'});
                this.listRef.current.onKeyDown(
                    keyDownEvent as unknown as React.KeyboardEvent<HTMLDivElement>,
                );
            }
        }
    }, this.props.debounce);

    componentDidMount() {
        const {getItemsOnMount, text} = this.props;
        if (this.props.syncPopupOnResize) {
            window.addEventListener('resize', this.throttledForceUpdate);
        }
        if (getItemsOnMount) {
            this.getItems(text);
        }
    }

    componentDidUpdate(prevProps: SuggestProps): void {
        const {text, showItemsOnNoText} = this.props;
        if (text !== prevProps.text && (text || showItemsOnNoText)) {
            this.getItems(text);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.throttledForceUpdate);
        this.clearProgress();
    }

    render() {
        const {
            text,
            size,
            className,
            popupClassName,
            popupPlacement,
            disabled,
            autoComplete,
            autoFocus,
            placeholder,
            onBlur,
            pin,
            id,
            renderStyle,
            hasClear,
            children,
            inputClassName,
            error,
            applicableInputValue,
            controlProps,
        } = this.props;
        const {progress, items, focused} = this.state;

        const input = (
            <TextInput
                size={size}
                value={text}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                ref={this.inputRef}
                controlRef={this.textInputRef}
                onBlur={onBlur}
                onUpdate={this.onUpdate}
                onFocus={() => this.setState({focused: true})}
                onKeyDown={this.onInputKeyDown}
                className={b('input', inputClassName)}
                pin={pin}
                id={id}
                hasClear={hasClear}
                error={error}
                controlProps={{
                    role: 'combobox',
                    'aria-controls': `popup-${id}`,
                    'aria-autocomplete': applicableInputValue ? 'both' : 'list',
                    'aria-expanded': this.isPopupHasContent() && this.state.focused,
                    'aria-activedescendant':
                        this.state.activeIndex === undefined
                            ? undefined
                            : `${id}-list-item-${this.state.activeIndex}`,
                    ...controlProps,
                }}
            />
        );

        if (children) {
            return React.createElement(React.Fragment, undefined, children(input, items, progress));
        }

        return (
            <OutsideFocusHandlerContainer
                as="div"
                className={b(null, className)}
                enable={focused}
                onFocusOutside={this.handleClickOrFocusOutside}
            >
                {input}
                {this.isPopupHasContent() &&
                    (renderStyle === 'inline' ? (
                        <div className={b('inline-content')}>{this.renderPopupContent()}</div>
                    ) : (
                        <Popup
                            open={this.isPopupHasContent() && this.state.focused}
                            contentClassName={b('popup', popupClassName)}
                            style={
                                this.inputRef.current
                                    ? {width: this.inputRef.current.offsetWidth}
                                    : {}
                            }
                            anchorRef={this.inputRef}
                            placement={popupPlacement}
                            id={`popup-${id}`}
                            onOutsideClick={this.handleClickOrFocusOutside}
                        >
                            {this.renderPopupContent()}
                        </Popup>
                    ))}
            </OutsideFocusHandlerContainer>
        );
    }

    getItems(text: string) {
        this.setProgressWithDelay();
        this._getItems(text);
    }

    private setProgressWithDelay() {
        if (!this.state.progressTimerId) {
            this.setState({
                progressTimerId: setTimeout(() => this.setState({progress: true}), 300),
            });
        }
    }

    private clearProgress() {
        const {progressTimerId} = this.state;

        this.setState({progress: false});
        if (progressTimerId) {
            clearTimeout(progressTimerId);
            this.setState({progressTimerId: null});
        }
    }

    private handleClickOrFocusOutside = () => {
        this.setState((state) => {
            return state.focused ? {focused: false} : null;
        });
    };

    private getNoItemsMessage(text: string) {
        const {getNoItemsMessage} = this.props;
        return typeof getNoItemsMessage === 'function'
            ? getNoItemsMessage(text)
            : i18n('items_not_found', {text});
    }

    private renderItem = (item: T, _active: boolean, index: number) => (
        <div key={index} className={b('item')}>
            {this.props.renderItem!(item)}
        </div>
    );

    private onItemClick = (item: T, index?: number, fromKeyboard?: boolean) => {
        let focused = false;
        if (this.props.onItemClick) {
            focused = Boolean(this.props.onItemClick(item, index, fromKeyboard));
        }
        this.setState({focused});
    };

    private onChangeActive = (index?: number) => {
        this.setState({activeIndex: index});
    };

    private renderPopupContent() {
        const {text, virtualized, itemHeight, id} = this.props;
        const {progress, items} = this.state;

        if (progress) {
            return (
                <div className={b('loader')}>
                    <Loader />
                </div>
            );
        }

        if (items.length) {
            const style: CSSProperties = {};

            if (virtualized) {
                let height = 0;

                if (typeof itemHeight === 'function') {
                    items.forEach((item) => {
                        height += itemHeight(item);
                    });
                }

                if (typeof itemHeight === 'number') {
                    height = items.length * itemHeight;
                }

                if (height) {
                    style.height = height;
                }
            }

            return (
                <div className={b('list')} style={style}>
                    <List
                        filterable={false}
                        items={items}
                        renderItem={this.renderItem}
                        onItemClick={this.onItemClick}
                        virtualized={virtualized}
                        itemHeight={itemHeight}
                        ref={this.listRef}
                        id={`${id}-list`}
                        role={'listbox'}
                        onChangeActive={this.onChangeActive}
                    />
                </div>
            );
        }

        if (text) {
            return <div className={b('list', {empty: true})}>{this.getNoItemsMessage(text)}</div>;
        }

        return null;
    }

    private onUpdate = (text: string) => {
        const {onUpdate, showItemsOnNoText} = this.props;
        onUpdate(text);

        if (text && !this.state.focused) {
            this.setState({focused: true});
        }

        if (showItemsOnNoText || text) {
            this.getItems(text);
        }
    };

    private isPopupHasContent() {
        const {progress, items} = this.state;
        const {text, showNoItemsMessage = true} = this.props;

        return Boolean(progress || items.length || (text && showNoItemsMessage));
    }

    private onInputKeyDown = (event: React.KeyboardEvent) => {
        if (
            typeof this.props.onInputKeyDown === 'function' &&
            (!this.listRef.current || typeof this.listRef.current.getActiveItem() !== 'number')
        ) {
            this.props.onInputKeyDown(this.props.text, event);
        }

        if (
            event.key === 'Enter' &&
            this.props.applicableInputValue &&
            typeof this.props.onInputEnterKeyDown === 'function' &&
            (!this.listRef.current || typeof this.listRef.current.getActiveItem() !== 'number')
        ) {
            this.props.onInputEnterKeyDown(this.props.text, event);
        } else if (this.listRef.current) {
            this.listRef.current.onKeyDown(event as React.KeyboardEvent<HTMLDivElement>);
        }
        // Prevent outside form submission on Enter if Popup is opened. In order to submit the form, User has
        // to press Enter twice - first to select item and close the Popup, then to submit.
        if (event.key === 'Enter' && this.state.focused) {
            event.preventDefault();
        }

        if (event.key === 'Home' || event.key === 'End') {
            // List component calls event.preventDefault() as result TextInput ignores Home/End hits
            // and cursor is not moved appropriately, so we have to manage it manually
            this.moveCursorToEdge(event.key === 'Home', event.shiftKey);
            event.preventDefault();
        }
    };

    private moveCursorToEdge(moveToStart: boolean, selectMode: boolean) {
        if (!this.textInputRef.current) {
            return;
        }

        const input = this.textInputRef.current;
        const end = moveToStart ? 0 : input.value.length;
        if (end == input.selectionStart) {
            return;
        }
        const selStart = input.selectionStart! >= 0 ? Number(input.selectionStart) : end;
        const start = selectMode ? selStart : end;
        const [b, e] = _sortBy([start, end]);
        input.setSelectionRange(b, e, start > end ? 'backward' : 'forward');
    }
}
